import { ShippingAddressInput } from 'lib/types'
import { FetchResult } from '@apollo/client'
import { ServiceDependencies } from 'lib/core/services/magento'
import { EMPTY_STRING } from 'lib/core/resources/constants'
import { apolloClient } from 'lib/core/services/magento/client'
import gql from 'graphql-tag'
import { jsonToGraphQLQuery } from 'lib/core/resources/helpers/jsonToGraphql'

export interface SetShippingAddressOnCartAddress {
  firstname: string
  lastname: string
  company: string
  street: string[]
  city: string
  region: string
  postcode: string
  country_code: string
  telephone: string
  save_in_address_book: boolean
  [key: string]: any
}

export interface SetShippingAddressesOnCartMutationObject {
  mutation: {
    setShippingAddressesOnCart: {
      __args: {
        input: {
          cart_id: string
          shipping_addresses: [
            {
              customer_address_id?: number
              address?: SetShippingAddressOnCartAddress
              [key: string]: any
            }
          ]
          [key: string]: any
        }
      }
      [key: string]: any
    }
  }
}

export const setShippingAddressesOnCartMutationObject: SetShippingAddressesOnCartMutationObject = {
  mutation: {
    setShippingAddressesOnCart: {
      __args: {
        input: {
          cart_id: '',
          shipping_addresses: [
            {
              customer_address_id: 0,
              address: {
                firstname: '',
                lastname: '',
                company: '',
                street: [],
                city: '',
                region: '',
                postcode: '',
                country_code: '',
                telephone: '',
                save_in_address_book: false
              }
            }
          ]
        }
      }
    }
  }
}

interface SetShippingAddressesOnCartMutationInput {
  cartId: string
  shippingAddress: ShippingAddressInput
  [key: string]: any
}

export type SetShippingAddressesOnCartMutation = (input: SetShippingAddressesOnCartMutationInput) => Promise<FetchResult<any>>

export const createSetShippingAddressesOnCartMutation = ({
  getMutations,
  getQueries
}: ServiceDependencies): SetShippingAddressesOnCartMutation => async ({
  cartId,
  shippingAddress,
  ...restInputParams
}) => {
  const queries = getQueries()
  const mutation = getMutations().setShippingAddressesOnCartMutation
  mutation.mutation.setShippingAddressesOnCart.cart = {
    shipping_addresses: queries.shippingAddresses.shipping_addresses,
    prices: queries.cartPrices.prices
  }

  const {
    address,
    customer_address_id: addressId,
    cart_customer_note: cartCustomerNote,
    customer_notes: customerNotes,
    pickup_location_code: pickupLocationCode,
    ...shippingInputRest
  } = shippingAddress

  const {
    firstName,
    lastName,
    company,
    street,
    street2,
    city,
    regionCode,
    postalCode,
    countryCode,
    phoneNumber,
    saveInAddressBook,
    ...restAddressFields
  } = address ?? {}

  for (const restInputParamsKey in restInputParams) {
    mutation.mutation.setShippingAddressesOnCart.__args.input[restInputParamsKey] = restInputParams[restInputParamsKey]
  }

  mutation.mutation.setShippingAddressesOnCart.__args.input.cart_id = cartId

  if (addressId) {
    mutation.mutation.setShippingAddressesOnCart.__args.input.shipping_addresses[0].customer_address_id = addressId
  }

  const _shippingAddress: SetShippingAddressOnCartAddress = {
    firstname: firstName ?? EMPTY_STRING,
    lastname: lastName ?? EMPTY_STRING,
    company: company ?? EMPTY_STRING,
    street: [street ?? EMPTY_STRING, street2 ?? EMPTY_STRING],
    city: city ?? EMPTY_STRING,
    region: regionCode ?? EMPTY_STRING,
    postcode: postalCode ?? EMPTY_STRING,
    country_code: countryCode ?? EMPTY_STRING,
    telephone: phoneNumber ?? EMPTY_STRING,
    save_in_address_book: saveInAddressBook ?? false
  }

  for (const restAddressFieldsKey in restAddressFields) {
    // @ts-expect-error
    _shippingAddress[restAddressFieldsKey] = restAddressFields[restAddressFieldsKey]
  }

  mutation.mutation.setShippingAddressesOnCart.__args.input.shipping_addresses[0].address = _shippingAddress

  if (addressId) {
    delete mutation.mutation.setShippingAddressesOnCart.__args.input.shipping_addresses[0]?.address
  } else {
    delete mutation.mutation.setShippingAddressesOnCart.__args.input.shipping_addresses[0]?.customer_address_id
  }

  for (const shippingInputRestKey in shippingInputRest) {
    mutation.mutation.setShippingAddressesOnCart.__args.input.shipping_addresses[0][shippingInputRestKey] = shippingInputRest[shippingInputRestKey]
  }

  return apolloClient.mutate({
    mutation: gql`${jsonToGraphQLQuery(mutation)}`,
    fetchPolicy: 'no-cache'
  })
}
